import { isB2C } from '@/domains/core/settings/utils';
import { Label } from '@/domains/core/tamagoshiTailwind/components/Label/Label';
import { NewTopMdScores } from '@/domains/productDiscovery/ArticleReviews/components/NewTopMdScores/NewTopMdScores';
import {
  bestQualityOffer,
  topSaleLabel,
} from '@/domains/productDiscovery/ProductCard/translations';
import { IndexLabels } from '@/domains/productDiscovery/ProductPage/common/components/AddToCart/IndexLabels/IndexLabels';
import { ProductPromoLabels } from '@/domains/productDiscovery/ProductPage/common/components/ProductLabels/components/ProductPromoLabels/ProductPromoLabels';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { LazyCarbonScoreOverview } from '@/domains/sustainability/carbonScore/components/CarbonScoreOverview/LazyCarbonScoreOverview';
import { SecondHandProductConditionTag } from '@/domains/sustainability/secondHand/components/SecondHandProductConditionTag/SecondHandProductConditionTag';

/**
 * Component that displays the product labels.
 *
 * This component renders various labels and scores related to a product, including:
 * - Top sale label
 * - Best quality offer label
 * - Promotional labels
 * - Second hand labels
 * - New top MD scores
 * - Carbon score overview
 * - Index labels (Energy and Repairability Labels)
 *
 * The logic and priority of the labels can be found in the following Jira ticket:
 * https://manomano.atlassian.net/browse/SMAR-1538
 *
 * @returns {JSX.Element} A div containing the product labels.
 */
export const ProductLabels = (): JSX.Element => {
  const { isOnTopSale, isBestQualityOffer } = useDetail();

  return (
    <div
      className="flex flex-wrap items-end gap-s empty:hidden"
      data-testid="product-labels"
    >
      {isB2C() && (
        <>
          {isOnTopSale ? (
            <Label text={topSaleLabel} variant="BrandSecondary" />
          ) : isBestQualityOffer ? (
            <Label text={bestQualityOffer} variant="BrandSecondary" />
          ) : null}
        </>
      )}
      <ProductPromoLabels />
      <SecondHandProductConditionTag />
      {isB2C() && <NewTopMdScores />}
      <LazyCarbonScoreOverview />
      <IndexLabels />
    </div>
  );
};
